import { useState, useEffect } from 'react';
import { Settings as SettingsIcon, Mail, Save, CheckCircle, AlertCircle } from 'lucide-react';
import axiosInstance from '../config/axiosConfig';

export default function Settings() {
  const [emailNotifications, setEmailNotifications] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [user, setUser] = useState(null);

  useEffect(() => {
    // Get user info
    const userStr = localStorage.getItem('spes_user');
    if (userStr) {
      try {
        setUser(JSON.parse(userStr));
      } catch (e) {
        console.error('Error parsing user data:', e);
      }
    }

    // Fetch current settings
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get('/notifications/settings');
      setEmailNotifications(response.data.email_notifications_enabled);
    } catch (error) {
      console.error('Failed to fetch settings:', error);
      setMessage({ type: 'error', text: 'Failed to load settings' });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);

    try {
      await axiosInstance.post('/notifications/settings', {
        email_notifications_enabled: emailNotifications
      });
      
      setMessage({ 
        type: 'success', 
        text: 'Settings saved successfully!' 
      });

      // Clear message after 3 seconds
      setTimeout(() => setMessage(null), 3000);
    } catch (error) {
      console.error('Failed to save settings:', error);
      setMessage({ 
        type: 'error', 
        text: 'Failed to save settings. Please try again.' 
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-navy p-6">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="bg-navy-light border border-teal/30 rounded-lg shadow-lg p-6 mb-6">
          <div className="flex items-center space-x-3 mb-2">
            <SettingsIcon className="w-8 h-8 text-gold" />
            <h1 className="text-3xl font-heading font-bold text-transparent bg-clip-text bg-gradient-to-r from-teal to-gold">
              Settings
            </h1>
          </div>
          {user && (
            <p className="text-textLight/60 text-sm">
              Manage your notification preferences and account settings
            </p>
          )}
        </div>

        {/* Settings Card */}
        <div className="bg-navy-light border border-teal/30 rounded-lg shadow-lg p-6">
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-4 border-teal border-t-transparent mx-auto mb-4"></div>
              <p className="text-textLight/60">Loading settings...</p>
            </div>
          ) : (
            <>
              {/* Notification Settings Section */}
              <div className="mb-8">
                <h2 className="text-xl font-heading font-semibold text-gold mb-4 flex items-center space-x-2">
                  <Mail className="w-5 h-5" />
                  <span>Notification Preferences</span>
                </h2>

                <div className="bg-navy/50 border border-teal/20 rounded-lg p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="text-lg font-medium text-textLight mb-2">
                        Email Notifications
                      </h3>
                      <p className="text-textLight/70 text-sm mb-4">
                        Receive email alerts for JD matches, follow-up reminders, and job assignments.
                        You will always receive in-app notifications regardless of this setting.
                      </p>

                      <div className="bg-teal/10 border border-teal/30 rounded-lg p-4 mb-4">
                        <p className="text-textLight/80 text-sm">
                          <strong className="text-teal">Note:</strong> Email notifications are sent to: <span className="text-gold font-medium">{user?.email}</span>
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Toggle Switch */}
                  <div className="flex items-center justify-between bg-navy border border-teal/30 rounded-lg p-4">
                    <div>
                      <p className="text-textLight font-medium">Enable Email Notifications</p>
                      <p className="text-textLight/60 text-sm">
                        {emailNotifications ? 'You will receive email alerts' : 'Email alerts are disabled'}
                      </p>
                    </div>

                    <button
                      onClick={() => setEmailNotifications(!emailNotifications)}
                      className={`relative inline-flex h-8 w-14 items-center rounded-full transition-colors ${
                        emailNotifications ? 'bg-teal' : 'bg-textLight/30'
                      }`}
                    >
                      <span
                        className={`inline-block h-6 w-6 transform rounded-full bg-white transition-transform ${
                          emailNotifications ? 'translate-x-7' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>
                </div>
              </div>

              {/* Message Display */}
              {message && (
                <div className={`mb-6 p-4 rounded-lg border flex items-center space-x-3 ${
                  message.type === 'success'
                    ? 'bg-teal/10 border-teal/30 text-teal'
                    : 'bg-red-500/10 border-red-500/30 text-red-400'
                }`}>
                  {message.type === 'success' ? (
                    <CheckCircle className="w-5 h-5" />
                  ) : (
                    <AlertCircle className="w-5 h-5" />
                  )}
                  <p>{message.text}</p>
                </div>
              )}

              {/* Save Button */}
              <button
                onClick={handleSave}
                disabled={saving}
                className="w-full flex items-center justify-center space-x-2 bg-gradient-to-r from-teal to-teal-light hover:from-teal-light hover:to-teal text-white font-semibold py-3 px-6 rounded-lg transition-all duration-300 shadow-lg hover:shadow-teal/40 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                    <span>Saving...</span>
                  </>
                ) : (
                  <>
                    <Save className="w-5 h-5" />
                    <span>Save Settings</span>
                  </>
                )}
              </button>
            </>
          )}
        </div>

        {/* Additional Info */}
        <div className="mt-6 bg-navy-light/50 border border-teal/20 rounded-lg p-4">
          <p className="text-textLight/60 text-sm">
            <strong className="text-teal">Tip:</strong> You can change these settings at any time. 
            In-app notifications will always be available in the bell icon at the top of the page.
          </p>
        </div>
      </div>
    </div>
  );
}
